import React from 'react';
import { PageHeader } from '@ant-design/pro-layout';
import { UserRole, useRoleGuard } from 'shared/hooks/use-role-guard.ts';
import { round100 } from 'shared/utils/numbers/round.ts';
import { ECurrency } from 'types/currencies';
import { OrderStatus } from 'types/order';
import { CargoCreate } from 'components/cargos/cargo-create';
import { StatisticMoney } from 'components/common/statistic-money/statistic-money';
import { OrderAddAdditionalCost } from 'components/orders/order-add-additional-cost';
import { OrderEdit } from 'components/orders/order-edit';
import { OrderHasMainInvoiceIcon } from 'components/orders/order-has-main-invoice-icon';
import { AdditionalCostsStatistic } from 'components/orders/order-page-header/components/additional-costs-statistic';
import { OrderStatusEdit } from 'components/orders/order-status-edit';
import { OrderStatusTag } from 'components/orders/order-status-tag';
import styles from './order-page-header.module.scss';
import type { IOrderPageHeaderProps } from './types';

export const OrderPageHeader: React.FC<IOrderPageHeaderProps> = (props) => {
  const { order } = props;

  const allowFinancialStatistics = useRoleGuard([
    UserRole.admin,
    UserRole.assistant,
    UserRole.accountant,
    UserRole.customer_manager,
  ]);

  const isAllowValueByMap = useRoleGuard([UserRole.admin]);

  const profit =
    order.freightInDefault && order.costPrice
      ? round100(
          order.freightInDefault +
            (order.documentPriceInDefault ?? 0) +
            (order.fineInDefault ?? 0) -
            (order.costPrice ?? 0) -
            (order.additionalCost ?? 0),
        )
      : 0;

  return (
    <PageHeader
      title={order.compositeId}
      subTitle={
        <OrderHasMainInvoiceIcon
          isShow={order.status == OrderStatus.delivered}
          hasMainInvoice={!!order.mainInvoiceId}
        />
      }
      extra={[
        <OrderAddAdditionalCost key='additional-cost' orderId={order.id} />,
        <OrderStatusEdit key='status-edit' orderId={order.id} currentStatus={order.status} />,
        <CargoCreate key='cargo-create' orderId={order.id} />,
        <OrderEdit key='order-edit' order={order} />,
      ]}
      tags={[<OrderStatusTag key={order.id} status={order.status} />]}
      onBack={() => history.back()}>
      <div className={styles.container}>
        <div></div>
        <div className={styles.statisticsBox}>
          {allowFinancialStatistics ? (
            <>
              <StatisticMoney
                title='Freight'
                currency={order.currency}
                value={order.freight}
                valueInDefault={order.freightInDefault}
              />
              <StatisticMoney
                title='Docs'
                currency={order.currency}
                value={order.documentPrice}
                valueInDefault={order.documentPriceInDefault}
              />
              <StatisticMoney title='Cost' currency={ECurrency.EUR} value={order.costPrice} />
              {isAllowValueByMap && (
                <StatisticMoney title='Cost by map' currency={ECurrency.EUR} value={order.costPriceByMap} />
              )}
              {order.fine ? (
                <StatisticMoney
                  title='Fine'
                  currency={order.currency}
                  value={order.fine}
                  valueInDefault={order.fineInDefault}
                />
              ) : null}
              <AdditionalCostsStatistic orderId={order.id} />
              <StatisticMoney title='Profit' currency={ECurrency.EUR} value={profit} />
            </>
          ) : null}
        </div>
      </div>
    </PageHeader>
  );
};
